import { Node, Slice } from 'prosemirror-model'
import { EditorState, Transaction } from 'prosemirror-state'
import { ReplaceStep } from 'prosemirror-transform'
import { schema, TrackedChangeType, TrackedChange } from 'prose-core'

import { LayerChange } from 'providers/LayerChangesProvider/types'
import InsertionIcon from 'assets/icons/changes/addIcon.svg'
import DeletionIcon from 'assets/icons/changes/deleteIcon.svg'
import FormattingIcon from 'assets/icons/changes/formattingIcon.svg'

import { Context } from './Types'
import { DATA_TYPE_ATTR } from './constants'

export const ACTIONS = {
  [TrackedChangeType.Insertion]: 'added',
  [TrackedChangeType.Deletion]: 'deleted',
  [TrackedChangeType.FormattingChange]: 'formatted',
  [TrackedChangeType.NodeTypeChange]: 'formatted',
}

export const ICONS = {
  [TrackedChangeType.Insertion]: InsertionIcon,
  [TrackedChangeType.Deletion]: DeletionIcon,
  [TrackedChangeType.FormattingChange]: FormattingIcon,
  [TrackedChangeType.NodeTypeChange]: FormattingIcon,
}

export const formattingImage = (step: ReplaceStep, state: EditorState) => {
  const oldNode = state.doc.nodeAt(step.from)
  const newNode = step.slice.content.firstChild

  return (
    step.to - step.from === 1 &&
    step.slice.size === 1 &&
    oldNode?.type === schema.nodes.image &&
    newNode?.type === schema.nodes.image &&
    oldNode.attrs.src === newNode.attrs.src
  )
}

export const deletingEmptyParagraphBeforeNonEmptyParagraph = (tr: Transaction) => {
  if (tr.steps.length !== 1) {
    return false
  }

  const step = tr.steps[0]
  if (!(step instanceof ReplaceStep)) {
    return false
  }

  const { $anchor } = tr.selection
  return $anchor.node().type === schema.nodes.paragraph && step.slice.size === 0 && step.to - step.from === 2
}

export const insertingEmptyParagraphBeforeDeletedEmptyParagraph = (tr: Transaction) => {
  if (tr.steps.length !== 1) {
    return false
  }

  const step = tr.steps[0]
  if (!(step instanceof ReplaceStep)) {
    return false
  }

  const { $anchor } = tr.selection

  const isInsertionEmptyParagraph =
    $anchor.node().type === schema.nodes.paragraph &&
    step.slice.size === 2 &&
    step.slice.content.content.every((node: Node) => node.type === schema.nodes.paragraph) &&
    step.slice.content.content[0].attrs.blockId != null &&
    step.to - step.from === 0

  return isInsertionEmptyParagraph
}

export function isNodeDeletedParagraph(node: Node<any> | null | undefined): node is Node {
  if (node == null) return false
  const isNodeParagraph = node.type === schema.nodes.paragraph
  return isNodeParagraph && isDeletion(node)
}

export const TRACKABLE_MARKS = [
  schema.marks.font_size.name,
  schema.marks.colored.name,
  schema.marks.em.name,
  schema.marks.link.name,
  schema.marks.strike.name,
  schema.marks.underline.name,
  schema.marks.font_weight.name,
  schema.marks.font_family.name,
  schema.marks.strong.name,
  schema.marks.subscript.name,
  schema.marks.superscript.name,
  schema.marks.highlighted.name,
  schema.marks.code.name,
]

export const isTrackedNode = (node: Node) => {
  return (
    node.marks.some(({ type }) => {
      const { deletion, insertion, formatting_change } = schema.marks
      return type === deletion || type === insertion || type === formatting_change
    }) || node.attrs.trackedChanges?.length > 0
  )
}

export const singleTagOpenDepthSlice = (slice: Slice) => {
  return slice.size === 1 && (slice.openStart === 1 || slice.openEnd === 1)
}

export const removeTrackedChangeFromNode = (tr: Transaction, node: Node, pos: number, type: TrackedChangeType) => {
  tr.setNodeMarkup(
    pos,
    // @ts-ignore
    null,
    {
      ...node.attrs,
      trackedChanges: node.attrs.trackedChanges.filter((trackedChange: TrackedChange) => trackedChange.type !== type),
    },
    node.marks,
  )
}

export const isInsertion = (node: Node) => {
  return (
    node.marks.some((mark) => mark.type === node.type.schema.marks.insertion) ||
    node.attrs.trackedChanges?.some(({ type }: TrackedChange) => type === TrackedChangeType.Insertion)
  )
}

export const isDeletion = (node: Node) => {
  return (
    !!node.marks.some((mark) => mark.type === node.type.schema.marks.deletion) ||
    !!node.attrs.trackedChanges?.some(({ type }: TrackedChange) => type === TrackedChangeType.Deletion)
  )
}

export const getDeletionAttrs = (node: Node): Record<string, any>[] => {
  return (
    (node.marks.filter((mark) => mark.type === node.type.schema.marks.deletion).map((mark) => mark.attrs) ||
      node.attrs.trackedChanges?.filter(({ type }: TrackedChange) => type === TrackedChangeType.Deletion)) ??
    []
  )
}

export function extractTrackedChanges(doc: Node): LayerChange[] {
  const trackedNodes: Node[] = []

  doc.descendants((node) => {
    if (isTrackedNode(node)) {
      trackedNodes.push(node)
    }
  })

  return trackedNodes.flatMap(nodeTrackedChanges)
}

export function groupChangesByUuid(changes: LayerChange[]) {
  const orderedChangesUuid: string[] = []
  const groupedChanges: { [key: string]: LayerChange } = {}

  changes.forEach((change) => {
    if (!orderedChangesUuid.includes(change.uuid)) {
      orderedChangesUuid.push(change.uuid)
    }

    if (!groupedChanges[change.uuid]) {
      groupedChanges[change.uuid] = change
    } else {
      groupedChanges[change.uuid].nodes = groupedChanges[change.uuid].nodes.concat(change.nodes)
    }
  })

  return orderedChangesUuid.map((uuid) => groupedChanges[uuid])
}

export function groupChangesByUserId(changes: LayerChange[]) {
  const groupedChanges: { [userId: string]: LayerChange[] } = {}

  changes.forEach((change) => {
    if (!change.userId) {
      return
    }
    if (!groupedChanges[change.userId]) {
      groupedChanges[change.userId] = [{ ...change }]
    } else {
      const groupedChangeIndex = groupedChanges[change.userId].findIndex(
        (groupedChange) => groupedChange.uuid === change.uuid,
      )
      if (groupedChangeIndex !== -1) {
        groupedChanges[change.userId][groupedChangeIndex].nodes = groupedChanges[change.userId][
          groupedChangeIndex
        ].nodes.concat(change.nodes)
      } else {
        groupedChanges[change.userId] = groupedChanges[change.userId].concat({ ...change })
      }
    }
  })

  return groupedChanges
}

function nodeTrackedChanges(node: Node) {
  const layerChanges: LayerChange[] = []

  node.marks
    .filter(({ type }) => ['deletion', 'insertion', 'formatting_change'].includes(type.name))
    .forEach((mark) => {
      const { userId, createdAt, after, before, uuid } = mark.attrs
      layerChanges.push({
        type: mark.type.name as TrackedChangeType,
        nodes: [node],
        userId,
        createdAt,
        before,
        after,
        uuid,
      })
    })

  const { trackedChanges } = node.attrs
  trackedChanges?.forEach((trackedChange: TrackedChange) => {
    const { userId, createdAt, after, before, type, uuid } = trackedChange

    layerChanges.push({
      type,
      userId,
      uuid,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      createdAt: createdAt!,
      before,
      after,
      nodes: [node],
    })
  })

  return layerChanges
}

export const setPreservedSelectionForTrackedChanges = (tr: Transaction) => {
  tr.setMeta('preservedSelectionForTrackedChanges', tr.selection)
}

export const getPreservedSelection = (tr: Transaction) => {
  return tr.getMeta('preservedSelectionForTrackedChanges')
}

/**
 * If the node is a deletion, then return true if all of its descendants are also deletions.
 *
 * @param {Node} node - The node to check.
 * @returns A boolean value.
 */
export const entirelyDeleted = (node: Node) => {
  if (!isDeletion(node)) {
    return false
  }

  let result = true
  node.descendants((child) => {
    if (!isDeletion(child)) {
      result = false
    }
  })
  return result
}

export const isNodeName = (name: string): name is keyof typeof schema.nodes => {
  return Object.keys(schema.nodes).includes(name)
}

export const setTrackedChangeContext = (tr: Transaction, context: Context) => {
  tr.setMeta('trackedChangeContext', context)
}

export const getContext = (tr: Transaction) => {
  return tr.getMeta('trackedChangeContext')
}

/**
 * It returns the element that was clicked on, or the closest ancestor of that element that has a
 * data-type attribute with the value tracked-change
 * @param {Event} event - Event - the event object that was triggered
 * @returns The element that was clicked on.
 */
export const targetElement = (event: Event) => {
  const element = event.target as HTMLElement
  return element.dataset.type === 'tracked-change'
    ? element
    : (element.closest(`[data-type='${DATA_TYPE_ATTR}']`) as HTMLElement)
}

export function isParagraph(node: Node<any> | null | undefined): node is Node<any> {
  return node != null && node.type === schema.nodes.paragraph
}

export function isHeading(node: Node<any> | null | undefined): node is Node<any> {
  return node != null && node.type === schema.nodes.heading
}

export function isEmptyParagraph(node: Node<any> | null | undefined) {
  return isParagraph(node) && node.nodeSize === 2
}

export function isEmptyInsertionParagraph(node: Node<any> | null | undefined) {
  return isParagraph(node) && isEmptyParagraph(node) && isInsertion(node)
}
